// src/components/shared/BrandIcons.tsx
// Brand icon system — deliberately NOT feather/lucide-shaped.
//
// Rules of the system:
//   · 24px grid, geometric construction (circles, squares, 45° cuts)
//   · 1.5 stroke, SQUARE terminals and MITRE joins — engineered, not soft
//   · every glyph carries one solid "ink" element (the duotone accent) so the
//     set reads as drawn, not traced
//   · API-compatible with lucide-react (size, className, ...svg props)
import React from "react";

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  size?: number | string;
}

/** stroke layer + solid ink layer */
const glyph = (
  { size = 24, ...rest }: IconProps,
  strokes: React.ReactNode,
  ink?: React.ReactNode
) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="square"
    strokeLinejoin="miter"
    aria-hidden="true"
    {...rest}
  >
    {strokes}
    {ink ? <g fill="currentColor" stroke="none">{ink}</g> : null}
  </svg>
);

/** solid-only glyph (brand marks) */
const solid = ({ size = 24, ...rest }: IconProps, children: React.ReactNode) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    {...rest}
  >
    {children}
  </svg>
);

/* ---------- brand mark ---------- */

export const Logomark: React.FC<IconProps & { spark?: string }> = ({
  size = 28,
  spark,
  ...rest
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    aria-hidden="true"
    {...rest}
  >
    <path
      d="M5 25.5V9l8.5 9.5L22 9v16.5"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="27" cy="7.5" r="2.2" fill={spark || "var(--primary, currentColor)"} />
  </svg>
);

/* ---------- direction ---------- */

export const ArrowLeft: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M20 12H6" /><path d="M11 6.5 5.5 12l5.5 5.5" /></>, <circle cx="20" cy="12" r="1.6" />);

export const ArrowRight: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M4 12h14" /><path d="m13 6.5 5.5 5.5-5.5 5.5" /></>, <circle cx="4" cy="12" r="1.6" />);

export const ArrowUp: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M12 20V6" /><path d="m6.5 11 5.5-5.5 5.5 5.5" /></>, <circle cx="12" cy="20" r="1.6" />);

export const ArrowDown: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M12 4v14" /><path d="m6.5 13 5.5 5.5 5.5-5.5" /></>, <circle cx="12" cy="4" r="1.6" />);

export const ChevronLeft: React.FC<IconProps> = (p) =>
  glyph(p, <path d="m15 5.5-6.5 6.5 6.5 6.5" />);

export const ChevronRight: React.FC<IconProps> = (p) =>
  glyph(p, <path d="m9 5.5 6.5 6.5-6.5 6.5" />);

export const ChevronDown: React.FC<IconProps> = (p) =>
  glyph(p, <path d="m5.5 9 6.5 6.5L18.5 9" />);

export const ExternalLink: React.FC<IconProps> = (p) =>
  glyph(
    p,
    <><path d="M19 13.5V20H4V5h6.5" /><path d="M20 4 12 12" /></>,
    <path d="M13.5 3h7.5v7.5l-2-2V6.4l-3.5 3.5-1.4-1.4L18.1 5h-2.6z" />
  );

/* ---------- interface ---------- */

export const Menu: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M3.5 6.5h17" /><path d="M3.5 12h17" /></>, <rect x="3.5" y="16.5" width="10" height="1.6" />);

export const X: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="m5.5 5.5 13 13" /><path d="m18.5 5.5-13 13" /></>);

export const Plus: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M12 4.5v15" /><path d="M4.5 12h15" /></>);

export const Check: React.FC<IconProps> = (p) =>
  glyph(p, <path d="m4.5 12.5 5 5L19.5 7.5" />);

export const Search: React.FC<IconProps> = (p) =>
  glyph(p, <><circle cx="10.5" cy="10.5" r="6" /><path d="m15 15 5.5 5.5" /></>, <rect x="7.5" y="9.7" width="6" height="1.5" />);

export const ZoomIn: React.FC<IconProps> = (p) =>
  glyph(p, <><circle cx="10.5" cy="10.5" r="6" /><path d="m15 15 5.5 5.5" /></>, <><rect x="7.5" y="9.75" width="6" height="1.5" /><rect x="9.75" y="7.5" width="1.5" height="6" /></>);

export const Filter: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M3.5 5.5h17" /><path d="M6.5 12h11" /></>, <rect x="9.5" y="17.7" width="5" height="1.6" />);

export const GripVertical: React.FC<IconProps> = (p) =>
  glyph(p, undefined, <><rect x="8" y="5" width="2.2" height="2.2" /><rect x="13.8" y="5" width="2.2" height="2.2" /><rect x="8" y="10.9" width="2.2" height="2.2" /><rect x="13.8" y="10.9" width="2.2" height="2.2" /><rect x="8" y="16.8" width="2.2" height="2.2" /><rect x="13.8" y="16.8" width="2.2" height="2.2" /></>);

export const RefreshCw: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M20 12a8 8 0 0 1-13.7 5.6" /><path d="M4 12a8 8 0 0 1 13.7-5.6" /></>, <><path d="M17 3.5h3.5V7z" /><path d="M7 20.5H3.5V17z" /></>);

export const Save: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M4 4h12.5L20 7.5V20H4z" /><path d="M7.5 4v5H15V4" /></>, <rect x="7.5" y="13" width="9" height="7" />);

export const Copy: React.FC<IconProps> = (p) =>
  glyph(p, <><rect x="9" y="9" width="11" height="11" /><path d="M15 5.5H4v11h1.5" /></>, <rect x="12" y="13.7" width="5" height="1.6" />);

export const Edit: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M16.5 4.5 20 8 8.5 19.5 4 20.5l1-4.5z" /><path d="m14.5 6.5 3.5 3.5" /></>, <path d="M4 20.5 5 16l3.5 3.5z" />);

export const Trash2: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M4 6.5h16" /><path d="M9 6.5V3.5h6v3" /><path d="M6 6.5V20h12V6.5" /></>, <><rect x="9.3" y="10" width="1.5" height="6.5" /><rect x="13.2" y="10" width="1.5" height="6.5" /></>);

export const Download: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M12 3.5V15" /><path d="m6.5 10 5.5 5.5L17.5 10" /></>, <rect x="4" y="19" width="16" height="1.6" />);

export const Upload: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M12 15.5V4" /><path d="M6.5 9.5 12 4l5.5 5.5" /></>, <rect x="4" y="19" width="16" height="1.6" />);

export const Send: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M21 3 3 10l7 3.5z" /><path d="M21 3 14 21l-4-7.5z" /></>, <path d="M10 13.5 21 3l-7 18-4-7.5z" />);

export const Link: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M10 14a4 4 0 0 1 0-5.6l2.4-2.4a4 4 0 0 1 5.6 5.6L16.8 12.8" /><path d="M14 10a4 4 0 0 1 0 5.6l-2.4 2.4a4 4 0 0 1-5.6-5.6L7.2 11.2" /></>);

/* ---------- status ---------- */

export const AlertCircle: React.FC<IconProps> = (p) =>
  glyph(p, <circle cx="12" cy="12" r="8.5" />, <><rect x="11.2" y="6.5" width="1.6" height="7" /><rect x="11.2" y="15.4" width="1.6" height="1.8" /></>);

export const CheckCircle: React.FC<IconProps> = (p) =>
  glyph(p, <><circle cx="12" cy="12" r="8.5" /><path d="m7.8 12.2 2.9 2.9 5.5-5.5" /></>);

export const XCircle: React.FC<IconProps> = (p) =>
  glyph(p, <><circle cx="12" cy="12" r="8.5" /><path d="m9 9 6 6" /><path d="m15 9-6 6" /></>);

export const Eye: React.FC<IconProps> = (p) =>
  glyph(p, <path d="M2.5 12C5 7.8 8.3 5.7 12 5.7S19 7.8 21.5 12c-2.5 4.2-5.8 6.3-9.5 6.3S5 16.2 2.5 12z" />, <circle cx="12" cy="12" r="2.6" />);

export const EyeOff: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M9.6 6.1A9.6 9.6 0 0 1 12 5.7c3.7 0 7 2.1 9.5 6.3a17 17 0 0 1-2.6 3.4" /><path d="M6 7.5A16.6 16.6 0 0 0 2.5 12c2.5 4.2 5.8 6.3 9.5 6.3a9.4 9.4 0 0 0 3.4-.6" /><path d="m3.5 3.5 17 17" /></>, <circle cx="12" cy="12" r="2.2" />);

export const Lock: React.FC<IconProps> = (p) =>
  glyph(p, <><rect x="4.5" y="10.5" width="15" height="10" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /></>, <rect x="11.2" y="13.8" width="1.6" height="3.6" />);

export const Shield: React.FC<IconProps> = (p) =>
  glyph(p, <path d="M12 3.2 19.5 6v6c0 4.6-3 7.8-7.5 9-4.5-1.2-7.5-4.4-7.5-9V6z" />, <path d="M12 3.2 19.5 6v6c0 2.2-.7 4-1.9 5.4L12 12z" />);

/* ---------- objects ---------- */

export const Mail: React.FC<IconProps> = (p) =>
  glyph(p, <><rect x="3" y="5.5" width="18" height="13" /><path d="m3 6.5 9 6.5 9-6.5" /></>, <rect x="3" y="5.5" width="18" height="1.5" />);

export const Phone: React.FC<IconProps> = (p) =>
  glyph(p, <path d="M8.2 3.5 10.5 8 8 10.4a12.5 12.5 0 0 0 5.6 5.6L16 13.5l4.5 2.3-1.6 4.2a15.8 15.8 0 0 1-14.9-14.9z" />, <path d="M8.2 3.5 10.5 8 8 10.4 4 6.4z" />);

export const Calendar: React.FC<IconProps> = (p) =>
  glyph(p, <><rect x="3.5" y="5.5" width="17" height="15" /><path d="M3.5 10h17" /></>, <><rect x="6.8" y="3" width="1.6" height="4.4" /><rect x="15.6" y="3" width="1.6" height="4.4" /><rect x="7" y="12.8" width="4" height="4" /></>);

export const Clock: React.FC<IconProps> = (p) =>
  glyph(p, <circle cx="12" cy="12" r="8.5" />, <><rect x="11.25" y="6.5" width="1.5" height="6.2" /><rect x="11.25" y="11.3" width="5" height="1.5" /></>);

export const MapPin: React.FC<IconProps> = (p) =>
  glyph(p, <path d="M12 21C7.7 16.8 5.5 13.4 5.5 10a6.5 6.5 0 0 1 13 0c0 3.4-2.2 6.8-6.5 11z" />, <circle cx="12" cy="10" r="2.4" />);

export const Globe: React.FC<IconProps> = (p) =>
  glyph(p, <><circle cx="12" cy="12" r="8.5" /><path d="M3.5 12h17" /><path d="M12 3.5c3.4 3.8 3.4 13.2 0 17-3.4-3.8-3.4-13.2 0-17z" /></>, <circle cx="12" cy="12" r="1.5" />);

export const Home: React.FC<IconProps> = (p) =>
  glyph(p, <path d="M3.5 10.5 12 3.5l8.5 7v10h-17z" />, <rect x="9.5" y="13.5" width="5" height="7" />);

export const Image: React.FC<IconProps> = (p) =>
  glyph(p, <><rect x="3" y="4.5" width="18" height="15" /><path d="m3 16 5-5 4.5 4.5 3.5-3.5L21 16" /></>, <circle cx="8.2" cy="9" r="1.7" />);

export const Star: React.FC<IconProps> = (p) =>
  glyph(p, <path d="m12 3.5 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 10.1l6.2-.9z" />, <path d="m12 3.5 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3z" />);

export const Database: React.FC<IconProps> = (p) =>
  glyph(p, <><ellipse cx="12" cy="5.8" rx="7.5" ry="2.8" /><path d="M4.5 5.8v12.4c0 1.6 3.4 2.8 7.5 2.8s7.5-1.2 7.5-2.8V5.8" /><path d="M4.5 12c0 1.6 3.4 2.8 7.5 2.8s7.5-1.2 7.5-2.8" /></>, <ellipse cx="12" cy="5.8" rx="7.5" ry="2.8" />);

export const Briefcase: React.FC<IconProps> = (p) =>
  glyph(p, <><rect x="3" y="7.5" width="18" height="12.5" /><path d="M8.5 7.5V4.5h7v3" /></>, <rect x="3" y="11.5" width="18" height="1.6" />);

export const User: React.FC<IconProps> = (p) =>
  glyph(p, <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />, <circle cx="12" cy="7.8" r="3.8" />);

export const Users: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 13.6a6.5 6.5 0 0 1 5.5 6.4" /></>, <><circle cx="9" cy="8.2" r="3.4" /><circle cx="17" cy="8.5" r="2.6" /></>);

export const MessageSquare: React.FC<IconProps> = (p) =>
  glyph(p, <path d="M3.5 4.5h17v12h-11l-6 4.5z" />, <><rect x="7" y="8.5" width="10" height="1.5" /><rect x="7" y="11.7" width="6.5" height="1.5" /></>);

export const Settings: React.FC<IconProps> = (p) =>
  glyph(p, <><circle cx="12" cy="12" r="3.4" /><path d="M12 2.5v3.2" /><path d="M12 18.3v3.2" /><path d="M2.5 12h3.2" /><path d="M18.3 12h3.2" /><path d="m5.3 5.3 2.3 2.3" /><path d="m16.4 16.4 2.3 2.3" /><path d="m18.7 5.3-2.3 2.3" /><path d="m7.6 16.4-2.3 2.3" /></>, <circle cx="12" cy="12" r="1.4" />);

export const Wrench: React.FC<IconProps> = (p) =>
  glyph(p, <path d="M15.5 3.5a5 5 0 0 0-4.4 7.4L3.5 18.5 5.5 20.5l7.6-7.6a5 5 0 0 0 6.4-6.4l-3 3-2.4-2.4 3-3a5 5 0 0 0-1.6-.6z" />, <rect x="4.2" y="17.4" width="2.4" height="2.4" />);

export const Zap: React.FC<IconProps> = (p) =>
  glyph(p, <path d="M13.5 2.5 5 13.5h5.5L9.5 21.5 19 10.5h-5.5z" />, <path d="M13.5 2.5 5 13.5h5.5z" />);

export const Sparkles: React.FC<IconProps> = (p) =>
  glyph(p, <path d="m11 4 1.9 4.6L17.5 10.5l-4.6 1.9L11 17l-1.9-4.6L4.5 10.5l4.6-1.9z" />, <><path d="m18 14.5 .9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9z" /><rect x="17.3" y="3" width="1.4" height="4" /><rect x="15.5" y="4.3" width="4" height="1.4" /></>);

export const Rocket: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M12 15c5-3.7 7-8 6.6-11.6C15 3 10.7 5 7 10l-3.3.9 2.3 2.3 2.4 2.4 2.3 2.3z" /><path d="M6 16.5c-1.2.4-2 2-1.8 4.6 2.7.2 4.3-.6 4.7-1.8" /></>, <circle cx="14" cy="9.9" r="1.8" />);

export const DollarSign: React.FC<IconProps> = (p) =>
  glyph(p, <path d="M16.5 7.2A3.4 3.4 0 0 0 13.2 5h-2.4a3.1 3.1 0 0 0 0 6.2h2.4a3.4 3.4 0 0 1 0 6.8h-2.7a3.4 3.4 0 0 1-3.1-2" />, <rect x="11.25" y="2.5" width="1.5" height="19" />);

export const BarChart3: React.FC<IconProps> = (p) =>
  glyph(p, <path d="M3.5 20.5h17" />, <><rect x="5" y="13" width="3.4" height="6" /><rect x="10.3" y="8" width="3.4" height="11" /><rect x="15.6" y="4.5" width="3.4" height="14.5" /></>);

export const TrendingUp: React.FC<IconProps> = (p) =>
  glyph(p, <path d="m3 17.5 6-6 4 4 5.5-5.5" />, <path d="M14 8.5h6.5V15l-2.3-2.3-2-2z" />);

export const Code: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M8.5 6.5 3.5 12l5 5.5" /><path d="M15.5 6.5 20.5 12l-5 5.5" /></>, <rect x="11" y="5" width="1.6" height="14" transform="rotate(12 11.8 12)" />);

export const FolderTree: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M3.5 4v13.5h4" /><path d="M3.5 10h4" /></>, <><rect x="9" y="3" width="11.5" height="6" /><rect x="9" y="14.5" width="11.5" height="6" /></>);

export const Layers: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="m3 12.6 9 4.2 9-4.2" /><path d="m3 16.8 9 4.2 9-4.2" /></>, <path d="m12 3 9 4.4-9 4.4-9-4.4z" />);

export const Languages: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M2.5 5.5h10" /><path d="M10.8 5.5c-1 4.5-4 8.4-8 10.5" /><path d="M5 9.5c1.6 3 4.2 5.4 7.3 6.5" /></>, <><rect x="6.7" y="2.5" width="1.5" height="3.4" /><path d="m17.8 11 4.7 10.5h-1.9l-1.1-2.6h-3.4l-1.1 2.6h-1.9zm0 3.9-1 2.4h2z" /></>);

export const LogIn: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M13.5 4h6v16h-6" /><path d="M3.5 12h9" /><path d="m9 7.5 4.5 4.5L9 16.5" /></>, <rect x="17.5" y="4" width="2" height="16" />);

export const LogOut: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M10.5 4h-6v16h6" /><path d="M10.5 12h9" /><path d="m15 7.5 4.5 4.5L15 16.5" /></>, <rect x="4.5" y="4" width="2" height="16" />);

export const Moon: React.FC<IconProps> = (p) =>
  glyph(p, <path d="M20.5 14.8A8.8 8.8 0 1 1 9.2 3.5a7 7 0 0 0 11.3 11.3z" />, <path d="M20.5 14.8A8.8 8.8 0 0 1 12 20.8c4.8-2.6 6.6-7 5-13a7 7 0 0 0 3.5 7z" />);

export const Sun: React.FC<IconProps> = (p) =>
  glyph(p, <><path d="M12 2.5v2.8" /><path d="M12 18.7v2.8" /><path d="M2.5 12h2.8" /><path d="M18.7 12h2.8" /><path d="m5.2 5.2 2 2" /><path d="m16.8 16.8 2 2" /><path d="m18.8 5.2-2 2" /><path d="m7.2 16.8-2 2" /></>, <circle cx="12" cy="12" r="4" />);

export const Monitor: React.FC<IconProps> = (p) =>
  glyph(p, <><rect x="2.5" y="4" width="19" height="13" /><path d="M8.5 20.5h7" /></>, <rect x="11.2" y="17" width="1.6" height="3.5" />);

export const Smartphone: React.FC<IconProps> = (p) =>
  glyph(p, <rect x="6.5" y="2.5" width="11" height="19" />, <><rect x="9.5" y="5" width="5" height="1.4" /><rect x="10.6" y="17.6" width="2.8" height="1.6" /></>);

export const Tablet: React.FC<IconProps> = (p) =>
  glyph(p, <rect x="4" y="2.5" width="16" height="19" />, <rect x="10.4" y="17.8" width="3.2" height="1.6" />);

/* GitHub keeps its official silhouette — a brand glyph, not ours to redraw */
export const Github: React.FC<IconProps> = (p) =>
  solid(
    p,
    <path d="M12 1.75C6.2 1.75 1.5 6.45 1.5 12.25c0 4.64 3 8.58 7.18 9.97.53.1.72-.23.72-.5 0-.25-.01-1.08-.01-1.96-2.92.63-3.54-1.25-3.54-1.25-.48-1.21-1.17-1.54-1.17-1.54-.95-.65.07-.64.07-.64 1.06.08 1.61 1.08 1.61 1.08.94 1.6 2.46 1.14 3.06.87.1-.68.37-1.14.66-1.4-2.33-.27-4.79-1.17-4.79-5.19 0-1.15.41-2.09 1.08-2.82-.1-.27-.47-1.34.1-2.79 0 0 .89-.28 2.9 1.08a10.1 10.1 0 0 1 5.28 0c2.01-1.36 2.9-1.08 2.9-1.08.57 1.45.2 2.52.1 2.79.67.73 1.08 1.67 1.08 2.82 0 4.03-2.46 4.92-4.8 5.18.38.33.71.96.71 1.95 0 1.4-.01 2.54-.01 2.88 0 .28.19.61.73.5a10.51 10.51 0 0 0 7.16-9.96c0-5.8-4.7-10.5-10.5-10.5z" />
  );
